import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { getKitsConProductos, getBarriosActivos, crearMiSuscripcion, simularOpcionesPrimeraEntrega } from '../../lib/portalClienteApi'
import { geocodificar, validarBarrioConDireccion } from '../../lib/geo'
import MapPin from '../../components/MapPin'

/** Mapeo segmento de hogar → códigos de kits que aplican */
const SEGMENTOS = [
  {
    id: 'solo',
    titulo: 'Vivo solo o en pareja',
    sub: '1 a 2 personas',
    icono: '👤',
    detalle: 'Lo justo para mantener la casa al día sin que te sobre nada.',
    kits: ['solo', 'duo'],
  },
  {
    id: 'familia',
    titulo: 'Familia tipo',
    sub: '3 a 4 personas',
    icono: '👨‍👩‍👧',
    detalle: 'Cubre cocina, baño y limpieza general. Es nuestro perfil más elegido.',
    kits: ['familiar', 'confort'],
  },
  {
    id: 'familia_grande',
    titulo: 'Familia grande',
    sub: '5 o más personas · casa con perro/gato',
    icono: '🏡',
    detalle: 'Más cantidad y más variedad para que nunca se te termine nada.',
    kits: ['hogar_plus', 'exclusive'],
  },
  {
    id: 'comercio',
    titulo: 'Comercio u oficina',
    sub: 'Almacén, bar, estudio, oficina',
    icono: '🏢',
    detalle: 'Productos de uso intensivo pensados para uso comercial.',
    kits: ['comercio_local', 'estudio_oficina'],
  },
]

const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }

export default function NuevaSuscripcion() {
  const { user, profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  // Si no hay user logueado, redirigir a /login con destino de vuelta
  useEffect(() => {
    if (authLoading) return
    if (!user) {
      const next = encodeURIComponent(window.location.pathname || '/cliente/nueva-suscripcion')
      navigate(`/login?next=${next}`, { replace: true })
    }
  }, [user, authLoading, navigate])

  // 0 segmento · 1 kit · 2 dirección · 3 día · 4 confirmar
  const [paso, setPaso] = useState(0)
  const [kits, setKits] = useState([])
  const [barrios, setBarrios] = useState([])
  const [coords, setCoords] = useState(null)
  const [coordsExactas, setCoordsExactas] = useState(false)
  const [validacionBarrio, setValidacionBarrio] = useState(null)
  const [geocodando, setGeocodando] = useState(false)
  const [creando, setCreando] = useState(false)
  const [err, setErr] = useState(null)
  const [verTodos, setVerTodos] = useState(false)
  const [tierActivo, setTierActivo] = useState('essential')  // toggle dentro del paso 1
  const [kitExpandido, setKitExpandido] = useState(null)

  const [form, setForm] = useState({
    segmento: '',
    alias: '',
    kitCodigo: '',
    barrioId: '',
    direccion: '',
    apto: '',
    tipoVivienda: '',
    diaEntrega: '',
    ventana: 'manana',
    semanaMes: '',
    notas: '',
  })

  useEffect(() => {
    Promise.all([getKitsConProductos(), getBarriosActivos()]).then(([k, b]) => {
      setKits(k); setBarrios(b)
    })
  }, [])

  // Opciones de primera entrega (respeta D-2 y semana_mes)
  // Si dentro de corte: 1 opcion. Si fuera de corte: hasta 5 opciones.
  const [opcionesPrimera, setOpcionesPrimera] = useState([])
  const [opcionElegidaIdx, setOpcionElegidaIdx] = useState(0)
  useEffect(() => {
    let cancel = false
    if (!form.barrioId || !form.diaEntrega || !form.semanaMes) {
      setOpcionesPrimera([]); setOpcionElegidaIdx(0); return
    }
    simularOpcionesPrimeraEntrega({
      barrioId: form.barrioId,
      diaEntrega: form.diaEntrega,
      semanaMes: form.semanaMes,
    }).then(opts => {
      if (cancel) return
      setOpcionesPrimera(opts || [])
      setOpcionElegidaIdx(0) // por default elige la primera (esperar gratis)
    }).catch(() => { if (!cancel) { setOpcionesPrimera([]); setOpcionElegidaIdx(0) } })
    return () => { cancel = true }
  }, [form.barrioId, form.diaEntrega, form.semanaMes])

  const opcionElegida = opcionesPrimera[opcionElegidaIdx] || null
  const fueraDeCorte = opcionesPrimera.length > 1
  const recargoPrimera = Number(opcionElegida?.recargo || 0)

  const segmentoElegido = SEGMENTOS.find(s => s.id === form.segmento)
  const kitElegido = kits.find(k => k.codigo === form.kitCodigo)
  const barrioElegido = barrios.find(b => b.id === form.barrioId)
  const diasDisponibles = barrioElegido?.dias || []

  // Filtrar kits según segmento + tier (o todos si verTodos)
  const kitsVisibles = useMemo(() => {
    if (verTodos) return kits
    if (!segmentoElegido) return []
    const codigosOk = segmentoElegido.kits
    let filtrados = kits.filter(k => codigosOk.includes(k.codigo))
    // Si es comercio mostramos los 2 sin tabs; si no, filtramos por tier activo
    if (form.segmento !== 'comercio') {
      filtrados = filtrados.filter(k => k.tier === tierActivo)
    }
    return filtrados
  }, [kits, segmentoElegido, tierActivo, verTodos, form.segmento])

  async function geocodear() {
    setErr(null); setCoordsExactas(false); setValidacionBarrio(null)
    if (!barrioElegido) return
    setGeocodando(true)
    try {
      if (form.direccion.trim()) {
        // Geocoding sin forzar el barrio en la query (para detectar el real)
        const r = await geocodificar(form.direccion)
        if (r) {
          setCoords({ lat: r.lat, lng: r.lng })
          setCoordsExactas(true)
          const v = validarBarrioConDireccion(r.barrioDetectado, barrioElegido.nombre, barrios)
          setValidacionBarrio(v)
          return
        }
      }
      const rb = await geocodificar(barrioElegido.nombre)
      if (rb) setCoords({ lat: rb.lat, lng: rb.lng })
    } finally { setGeocodando(false) }
  }

  function aplicarSugerenciaBarrio() {
    if (!validacionBarrio?.sugerenciaActivo) return
    setForm(f => ({ ...f, barrioId: validacionBarrio.sugerenciaActivo.id, diaEntrega: '' }))
    setValidacionBarrio(null)
  }

  async function confirmar() {
    setErr(null); setCreando(true)
    try {
      if (!form.kitCodigo) throw new Error('Falta elegir kit')
      if (!form.barrioId) throw new Error('Falta elegir barrio')
      if (!form.direccion.trim()) throw new Error('Falta la dirección')
      if (!form.diaEntrega) throw new Error('Falta elegir día de entrega')
      if (!form.semanaMes) throw new Error('Falta elegir semana del mes')
      const diaCobroMes = new Date().getDate()
      const cliente = await crearMiSuscripcion({
        alias: form.alias,
        kitCodigo: form.kitCodigo,
        barrioId: form.barrioId,
        direccion: form.direccion,
        apto: form.apto,
        tipoVivienda: form.tipoVivienda,
        diaEntrega: form.diaEntrega,
        ventana: form.ventana,
        semanaMes: form.semanaMes,
        diaCobroMes,
        notas: form.notas,
        lat: coords?.lat, lng: coords?.lng,
        primeraEntregaFecha: opcionElegida?.fechaEntrega || null,
        primeraEntregaRecargo: recargoPrimera,
        primeraEntregaTipo: opcionElegida?.opcion || null,
      })
      // Despues de crear suscripcion, OBLIGATORIO activar cobro automatico via MP
      const clienteId = cliente?.id
      if (clienteId) {
        navigate(`/cliente/cargar-tarjeta/${clienteId}`, { replace: true })
      } else {
        navigate('/cliente', { replace: true })
      }
    } catch (e) { setErr(e.message); setCreando(false) }
  }

  async function logout() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  const nombre = profile?.nombre?.split(' ')[0] || ''
  const diaHoy = new Date().getDate()
  const totalPasos = 5  // 0..4

  // Sin user → el useEffect de arriba redirige a /login
  if (!user) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a3461', fontFamily: "'Inter', system-ui, sans-serif" }}>Redirigiendo…</div>
  }

  return (
    <div className="lp-app">
      {/* NAVBAR estilo web pública */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <Link to="/cliente" className="lp-logo">Lim<span>pack</span></Link>
          <div className="lp-nav-actions">
            <Link to="/cliente" className="lp-nav-link">← Mi cuenta</Link>
            <button onClick={logout} className="lp-nav-logout">Salir</button>
          </div>
        </div>
      </nav>

      {/* HERO degradé igual que la web */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-pill">
            <span className="lp-hero-pill-dot"></span>
            Paso {paso + 1} de {totalPasos} · Crear suscripción
          </div>
          <h1 className="lp-hero-h1">
            {paso === 0 && <>Armemos tu kit, <span>{nombre || 'hola'}</span></>}
            {paso === 1 && <>Elegí <span>tu kit</span></>}
            {paso === 2 && <>¿A dónde te lo <span>llevamos?</span></>}
            {paso === 3 && <>¿Cuándo te queda <span>mejor?</span></>}
            {paso === 4 && <>Confirmá tu <span>suscripción</span></>}
          </h1>
          <p className="lp-hero-sub">
            {paso === 0 && 'Primero contanos cómo es tu hogar — así te recomendamos el kit ideal.'}
            {paso === 1 && segmentoElegido && `Te mostramos los kits para ${segmentoElegido.titulo.toLowerCase()}.`}
            {paso === 2 && 'Dirección y barrio. Tenemos ruta fija — el envío es gratis.'}
            {paso === 3 && barrioElegido && `En ${barrioElegido.nombre} repartimos los días ${diasDisponibles.join(', ')}.`}
            {paso === 4 && 'Si está todo bien, creamos tu suscripción y te avisamos por WhatsApp.'}
          </p>
        </div>
      </section>

      {/* Stepper */}
      <div className="lp-stepper-wrap">
        <div className="lp-stepper">
          {['Hogar', 'Kit', 'Dirección', 'Día', 'Confirmar'].map((label, i) => (
            <div key={i} className={`lp-step ${paso === i ? 'active' : ''} ${paso > i ? 'done' : ''}`}>
              <div className="lp-step-num">{paso > i ? '✓' : i + 1}</div>
              <div className="lp-step-label">{label}</div>
            </div>
          ))}
        </div>
      </div>

      <main className="lp-main">

        {/* PASO 0: Segmentar por hogar */}
        {paso === 0 && (
          <div className="lp-grid-segs">
            {SEGMENTOS.map(s => {
              const elegido = form.segmento === s.id
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`lp-seg-card ${elegido ? 'is-elegido' : ''}`}
                  onClick={() => setForm({ ...form, segmento: s.id, kitCodigo: '' })}
                >
                  <div className="lp-seg-icon">{s.icono}</div>
                  <div className="lp-seg-titulo">{s.titulo}</div>
                  <div className="lp-seg-sub">{s.sub}</div>
                  <div className="lp-seg-det">{s.detalle}</div>
                  {elegido && <div className="lp-seg-check">✓ Seleccionado</div>}
                </button>
              )
            })}
            <BtnNav
              onNext={() => { setPaso(1); setTierActivo(form.segmento === 'comercio' ? 'comercial' : 'essential') }}
              disabled={!form.segmento}
            />
          </div>
        )}

        {/* PASO 1: Kits filtrados con toggle Essential/Premium */}
        {paso === 1 && (
          <div className="lp-card lp-card-pad">
            {/* Toggle solo si NO es comercio y tenemos un segmento */}
            {form.segmento !== 'comercio' && segmentoElegido && !verTodos && (
              <>
                <div className="lp-tier-info">
                  <strong>Essential</strong>: máximo ahorro con calidad comprobada.<br/>
                  <strong>Premium</strong>: las marcas que conocés y aromas que se sienten en la casa. Cottonmax 2-en-1, CIF, papel doble hoja — más rendimiento por bulto, más confort en cada uso.
                </div>
                <div className="lp-toggle-wrap">
                  <div className="lp-toggle">
                    <button className={tierActivo === 'essential' ? 'on' : ''} onClick={() => setTierActivo('essential')}>ESSENTIAL</button>
                    <button className={tierActivo === 'premium' ? 'on' : ''} onClick={() => setTierActivo('premium')}>PREMIUM</button>
                  </div>
                </div>
              </>
            )}

            {/* Lista de kits */}
            <div className="lp-kits">
              {kitsVisibles.map(k => {
                const elegido = form.kitCodigo === k.codigo
                const expandido = kitExpandido === k.codigo
                const esPremium = k.tier === 'premium'
                return (
                  <div key={k.codigo} className={`lp-kit ${elegido ? 'is-elegido' : ''} ${esPremium ? 'is-premium' : ''}`}>
                    {k.destacado && <div className="lp-kit-badge">⭐ Más popular</div>}
                    <div className="lp-kit-head">
                      <div className="lp-kit-titulo">
                        <h3>{k.nombre}</h3>
                        <span className={`lp-kit-tier tier-${k.tier}`}>{TIER_LABEL[k.tier] || k.tier}</span>
                      </div>
                      <div className="lp-kit-meta">{k.personas} · {k.productos.length} productos</div>
                      {k.descripcion && <p className="lp-kit-desc">{k.descripcion}</p>}
                    </div>

                    <div className="lp-kit-precio-row">
                      <div>
                        <div className="lp-kit-precio">${Number(k.precio_susc).toLocaleString('es-UY')}</div>
                        <div className="lp-kit-precio-sub">por mes · envío incluido</div>
                      </div>
                      <button type="button" className="lp-kit-ver" onClick={() => setKitExpandido(expandido ? null : k.codigo)}>
                        {expandido ? 'Ocultar productos ▴' : 'Ver qué incluye ▾'}
                      </button>
                    </div>

                    {expandido && (
                      <ul className="lp-kit-productos">
                        {k.productos.map((p, i) => (
                          <li key={i}>
                            <span className="lp-kit-prod-cant">{p.cantidad}×</span>
                            <span className="lp-kit-prod-nom">{p.nombre}</span>
                            {p.presentacion && <span className="lp-kit-prod-pres"> · {p.presentacion}</span>}
                            {p.marca && <span className="lp-kit-prod-marca"> · {p.marca}</span>}
                          </li>
                        ))}
                      </ul>
                    )}

                    <button
                      type="button"
                      className={elegido ? 'lp-kit-btn-on' : 'lp-kit-btn'}
                      onClick={() => setForm({ ...form, kitCodigo: k.codigo })}
                    >
                      {elegido ? '✓ Este es mi kit' : 'Quiero este kit'}
                    </button>
                  </div>
                )
              })}
            </div>

            {/* Toggle ver todos los kits */}
            <button type="button" className="lp-link-todos" onClick={() => setVerTodos(v => !v)}>
              {verTodos ? '← Volver a las recomendaciones' : 'Quiero ver todos los kits disponibles →'}
            </button>

            <BtnNav
              onBack={() => { setPaso(0); setKitExpandido(null) }}
              onNext={() => setPaso(2)}
              disabled={!form.kitCodigo}
            />
          </div>
        )}

        {/* PASO 2: Dirección */}
        {paso === 2 && (
          <div className="lp-card lp-card-pad">
            <Field label="Alias (opcional)">
              <input type="text" value={form.alias} onChange={e => setForm({ ...form, alias: e.target.value })} placeholder="Casa, oficina, mamá…" />
            </Field>

            <div className="lp-row-2">
              <Field label="Barrio" required>
                <select value={form.barrioId} onChange={e => setForm({ ...form, barrioId: e.target.value, diaEntrega: '' })}>
                  <option value="">— elegir —</option>
                  {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Field>
              <Field label="Tipo de vivienda">
                <select value={form.tipoVivienda} onChange={e => setForm({ ...form, tipoVivienda: e.target.value })}>
                  <option value="">— elegir —</option>
                  <option value="casa">Casa</option>
                  <option value="edificio">Edificio</option>
                  <option value="oficina">Oficina</option>
                </select>
              </Field>
            </div>

            {!form.barrioId && barrios.length > 0 && (
              <div className="lp-info">
                Por ahora cubrimos: {barrios.map(b => b.nombre).join(', ')}.
              </div>
            )}

            <div className="lp-row-2-3">
              <Field label="Calle y número" required>
                <input type="text" required value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} onBlur={geocodear} placeholder="Cavia 2858" />
              </Field>
              <Field label="Apto / Piso">
                <input type="text" value={form.apto} onChange={e => setForm({ ...form, apto: e.target.value })} placeholder="Apto 406" />
              </Field>
            </div>

            <button type="button" onClick={geocodear} disabled={!form.barrioId || geocodando} className="lp-btn-secondary">
              {geocodando ? 'Buscando…' : coords ? 'Recargar mapa' : 'Verificar en mapa'}
            </button>

            {/* Validación: barrio detectado vs barrio elegido */}
            {validacionBarrio && !validacionBarrio.ok && (
              <div className={`lp-warn-barrio ${validacionBarrio.sinCobertura ? 'is-bloqueo' : 'is-warn'}`}>
                <div className="lp-warn-barrio-icon">{validacionBarrio.sinCobertura ? '🚫' : '⚠️'}</div>
                <div className="lp-warn-barrio-body">
                  <strong>{validacionBarrio.mensaje}</strong>
                  {validacionBarrio.sinCobertura ? (
                    <>
                      <p>Por ahora no llegamos a esa zona. Si querés que te avisemos cuando lleguemos:</p>
                      <a href={`https://wa.me/59898610001?text=${encodeURIComponent('Hola! Mi dirección es ' + form.direccion + '. Me gustaría saber cuándo llegan a mi zona con Limpack.')}`}
                         target="_blank" rel="noopener" className="lp-warn-cta">
                        📲 Avisame por WhatsApp
                      </a>
                    </>
                  ) : validacionBarrio.sugerenciaActivo ? (
                    <>
                      <p>Para continuar, cambiá el barrio elegido o corregí la dirección.</p>
                      <button type="button" className="lp-warn-cta" onClick={aplicarSugerenciaBarrio}>
                        ✓ Cambiar a {validacionBarrio.sugerenciaActivo.nombre}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {coords && !validacionBarrio?.sinCobertura && (
              <>
                <div style={{ marginTop: 12 }}>
                  <MapPin lat={coords.lat} lng={coords.lng} onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }} height={220} />
                </div>
                <label className={`lp-confirm-pin ${coordsExactas ? 'ok' : 'pend'}`}>
                  <input type="checkbox" checked={coordsExactas} onChange={e => setCoordsExactas(e.target.checked)} />
                  <span>{coordsExactas ? 'Pin en la ubicación correcta' : 'Confirmá el pin antes de seguir'}</span>
                </label>
              </>
            )}

            <BtnNav
              onBack={() => setPaso(1)}
              onNext={() => setPaso(3)}
              disabled={
                !form.barrioId
                || !form.direccion.trim()
                || (validacionBarrio && !validacionBarrio.ok)
                || (coords && !validacionBarrio?.sinCobertura && !coordsExactas)
              } />
          </div>
        )}

        {/* PASO 3: Día */}
        {paso === 3 && (
          <div className="lp-card lp-card-pad">
            <Field label="Día de entrega" required>
              <select value={form.diaEntrega} onChange={e => setForm({ ...form, diaEntrega: e.target.value })}>
                <option value="">— elegir —</option>
                {diasDisponibles.map(d => <option key={d} value={d} style={{ textTransform: 'capitalize' }}>{d}</option>)}
              </select>
            </Field>

            <div className="lp-row-2">
              <Field label="Semana del mes" required>
                <select value={form.semanaMes} onChange={e => setForm({ ...form, semanaMes: e.target.value })}>
                  <option value="">— elegir —</option>
                  <option value="1">1ra (días 1-7)</option>
                  <option value="2">2da (días 8-14)</option>
                  <option value="3">3ra (días 15-21)</option>
                  <option value="4">4ta (días 22-31)</option>
                </select>
              </Field>
              <Field label="Ventana horaria">
                <select value={form.ventana} onChange={e => setForm({ ...form, ventana: e.target.value })}>
                  <option value="manana">Mañana 10-14hs</option>
                  <option value="tarde">Tarde 14-18hs</option>
                </select>
              </Field>
            </div>

            {opcionesPrimera.length === 1 && (
              <div className="lp-info" style={{ background: '#ecfdf5', borderColor: '#10b981', color: '#065f46' }}>
                📦 Tu primera entrega: <strong style={{ textTransform: 'capitalize' }}>{
                  new Date(opcionesPrimera[0].fechaEntrega + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
                }</strong>
              </div>
            )}

            {fueraDeCorte && (
              <div style={{ marginTop: 16, padding: '12px 14px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, color: '#78350f' }}>
                <strong>⚠️ Te quedaste afuera del corte para tu día elegido</strong>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  Como pediste después de las 16hs del día anterior al armado, no llegamos a tu día normal. Elegí cómo querés tu <strong>primera entrega</strong>:
                </div>
              </div>
            )}

            {fueraDeCorte && (
              <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                {opcionesPrimera.map((o, idx) => {
                  const fechaTxt = new Date(o.fechaEntrega + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
                  const elegida = idx === opcionElegidaIdx
                  let titulo, sub, badge
                  if (o.opcion === 'esperar') {
                    titulo = `📦 ${fechaTxt}`
                    sub = `Próximo día que pasamos por tu barrio. Desde el mes 2 vuelve a ${form.diaEntrega} ${form.semanaMes}ª semana.`
                    badge = { txt: 'Gratis', bg: '#10b981' }
                  } else if (o.opcion === 'saltar_corte') {
                    titulo = `💰 ${fechaTxt}`
                    sub = `Tu día elegido. Hacemos esfuerzo extra para llegar. Desde el mes 2 sin recargo.`
                    badge = { txt: `+$${o.recargo}`, bg: '#f59e0b' }
                  } else {
                    titulo = `💰 ${fechaTxt}`
                    sub = `Día fuera de tu ruta. Desde el mes 2 vuelve a ${form.diaEntrega} ${form.semanaMes}ª semana.`
                    badge = { txt: `+$${o.recargo}`, bg: '#f59e0b' }
                  }
                  return (
                    <button key={idx} type="button"
                      onClick={() => setOpcionElegidaIdx(idx)}
                      style={{
                        textAlign: 'left',
                        padding: '12px 14px',
                        border: elegida ? '2px solid #10b981' : '1px solid #d1d5db',
                        background: elegida ? '#ecfdf5' : '#fff',
                        borderRadius: 8,
                        cursor: 'pointer',
                        position: 'relative',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <strong style={{ textTransform: 'capitalize', fontSize: 15 }}>{titulo}</strong>
                        <span style={{ background: badge.bg, color: '#fff', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{badge.txt}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{sub}</div>
                    </button>
                  )
                })}
              </div>
            )}

            <div className="lp-info" style={{ marginTop: 16 }}>
              💳 Tu cobro mensual queda fijado al <strong>día {diaHoy}</strong> de cada mes (la fecha en que te suscribís).
            </div>

            <Field label="Notas para el repartidor (opcional)">
              <textarea value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} rows="2" placeholder="Llamar al timbre, portero hasta las 18hs, etc." />
            </Field>

            <BtnNav onBack={() => setPaso(2)} onNext={() => setPaso(4)} disabled={!form.diaEntrega || !form.semanaMes} />
          </div>
        )}

        {/* PASO 4: Confirmar */}
        {paso === 4 && (
          <div className="lp-card lp-card-pad">
            <div className="lp-resumen">
              <Row label="Hogar">{segmentoElegido?.titulo} · {segmentoElegido?.sub}</Row>
              <Row label="Kit"><strong>{kitElegido?.nombre}</strong> · ${Number(kitElegido?.precio_susc).toLocaleString('es-UY')} / mes</Row>
              <Row label="Dirección">{barrioElegido?.nombre} · {form.direccion}{form.apto ? ` · ${form.apto}` : ''}</Row>
              <Row label="Entrega habitual">
                <strong style={{ textTransform: 'capitalize' }}>{form.diaEntrega}</strong> de la <strong>{form.semanaMes}ª</strong> semana · {form.ventana === 'tarde' ? 'tarde 14-18hs' : 'mañana 10-14hs'}
              </Row>
              {opcionElegida && (
                <Row label="Primera entrega">
                  <strong style={{ textTransform: 'capitalize' }}>{
                    new Date(opcionElegida.fechaEntrega + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
                  }</strong>
                  {recargoPrimera > 0 && <span style={{ color: '#f59e0b', fontWeight: 600 }}> · +${recargoPrimera} {opcionElegida.opcion === 'saltar_corte' ? 'fuera de corte' : 'fuera de ruta'}</span>}
                </Row>
              )}
              <Row label="Cobro mensual">Día {diaHoy} de cada mes</Row>
              {form.alias && <Row label="Alias">{form.alias}</Row>}
              {form.notas && <Row label="Notas">{form.notas}</Row>}
            </div>

            <div className="lp-info-pago">
              <strong>💳 Cómo se paga</strong><br/>
              Al confirmar, te llevamos a <strong>Mercado Pago</strong> para activar el cobro automático mensual. Elegís tu método (tarjeta, MP wallet) y autorizás el débito. Cancelás cuando quieras.
            </div>

            {err && <div className="lp-err">{err}</div>}

            <BtnNav onBack={() => setPaso(3)} onNext={confirmar} nextLabel={creando ? 'Creando…' : '✓ Confirmar y crear suscripción'} disabled={creando} />
          </div>
        )}

        {/* Aviso de privacidad — visible en los 4 pasos */}
        <div className="lp-privacy">
          🔒 Tus datos están protegidos. Solo los usamos para procesar tu pedido y nunca los compartimos con terceros.
        </div>
      </main>

      {/* WhatsApp flotante igual que la web */}
      <a href="https://wa.me/59898610001?text=Hola!%20Tengo%20una%20duda%20sobre%20la%20suscripción"
         target="_blank" rel="noopener" className="lp-wa" aria-label="Contactar por WhatsApp">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* FOOTER idéntico a la web pública */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-grid">
            <div>
              <div className="lp-footer-logo">Lim<span>pack</span></div>
              <p className="lp-footer-desc">Productos de limpieza a domicilio por suscripción. Montevideo, Uruguay.</p>
            </div>
            <div>
              <div className="lp-footer-tit">Navegación</div>
              <ul className="lp-footer-list">
                <li><a href="https://limpack.uy/#como-funciona">Cómo funciona</a></li>
                <li><a href="https://limpack.uy/kits.html">Nuestros kits</a></li>
                <li><a href="https://limpack.uy/despensa.html">Despensa Limpack</a></li>
                <li><a href="https://limpack.uy/#zonas">Zonas de entrega</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-tit">Contacto</div>
              <ul className="lp-footer-list">
                <li><a href="https://wa.me/59898610001" target="_blank" rel="noopener">WhatsApp</a></li>
                <li><Link to="/cliente">Mi cuenta</Link></li>
                <li><a href="mailto:hola@limpack.uy">hola@limpack.uy</a></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bot">
            <p>© 2026 Limpack. Todos los derechos reservados.</p>
            <p>Montevideo, Uruguay · IVA incluido en todos los precios</p>
          </div>
        </div>
      </footer>

      <style>{styles}</style>
    </div>
  )
}

function Field({ label, children, required }) {
  return (
    <label className="lp-field">
      <b>
        {label} {required && <span className="lp-req">*</span>}
      </b>
      {children}
    </label>
  )
}

function Row({ label, children }) {
  return (
    <div className="lp-row">
      <span className="lp-row-tag">{label}</span>
      <span className="lp-row-val">{children}</span>
    </div>
  )
}

function BtnNav({ onBack, onNext, disabled, nextLabel = 'Siguiente →' }) {
  return (
    <div className="lp-nav-btns">
      <button className="lp-btn-back" onClick={onBack} style={{ visibility: onBack ? 'visible' : 'hidden' }}>← Atrás</button>
      <button className="lp-btn-next" onClick={onNext} disabled={disabled}>{nextLabel}</button>
    </div>
  )
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00c4d4;
  --cream: #F8FAFC;
  --ink:   #0f172a;
  --muted2:#64748b;
  --line2: #e2e8f0;
}

html, body { background: var(--cream); margin: 0 }
.lp-app {
  min-height: 100vh;
  background: var(--cream);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--navy);
  -webkit-font-smoothing: antialiased;
  display: flex; flex-direction: column;
}

/* Navbar */
.lp-nav {
  position: sticky; top: 0; z-index: 50;
  background: rgba(26,52,97,.94);
  backdrop-filter: blur(12px);
}
.lp-nav-inner {
  max-width: 1100px; margin: 0 auto;
  padding: 0 16px; height: 64px;
  display: flex; align-items: center; justify-content: space-between;
}
.lp-logo {
  font-size: 22px; font-weight: 900;
  color: #fff; text-decoration: none;
  letter-spacing: -.02em;
}
.lp-logo span { color: var(--teal) }
.lp-nav-actions { display: flex; gap: 10px; align-items: center }
.lp-nav-link {
  color: rgba(255,255,255,.75); text-decoration: none;
  font-size: 13px; font-weight: 500;
}
.lp-nav-link:hover { color: var(--teal) }
.lp-nav-logout {
  background: rgba(255,255,255,.12); color: #fff;
  border: 0; padding: 8px 14px; border-radius: 999px;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.lp-nav-logout:hover { background: rgba(255,255,255,.2) }

/* HERO */
.lp-hero {
  background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00c4d4 100%);
  padding: 56px 16px 70px;
  color: #fff;
}
.lp-hero-inner { max-width: 760px; margin: 0 auto; text-align: center }
.lp-hero-pill {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.18);
  padding: 7px 16px; border-radius: 999px;
  font-size: 12px; font-weight: 600; color: rgba(255,255,255,.92);
  margin-bottom: 18px;
}
.lp-hero-pill-dot {
  width: 7px; height: 7px; background: var(--teal); border-radius: 50%;
  animation: pulse 1.6s ease-in-out infinite;
}
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }

.lp-hero-h1 {
  font-size: clamp(28px, 5vw, 44px); font-weight: 900;
  line-height: 1.1; letter-spacing: -.02em; margin: 0 0 14px;
}
.lp-hero-h1 span { color: var(--teal) }
.lp-hero-sub {
  font-size: clamp(14px, 2.5vw, 17px);
  color: rgba(255,255,255,.82); line-height: 1.5;
  max-width: 560px; margin: 0 auto;
}

/* Stepper */
.lp-stepper-wrap {
  background: #fff; border-bottom: 1px solid var(--line2);
  padding: 16px;
}
.lp-stepper {
  max-width: 760px; margin: 0 auto;
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px;
}
.lp-step { text-align: center; opacity: .4; transition: opacity .2s }
.lp-step.active, .lp-step.done { opacity: 1 }
.lp-step-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 50%;
  background: #e2e8f0; color: #64748b;
  font-weight: 800; font-size: 13px; margin-bottom: 4px;
}
.lp-step.active .lp-step-num { background: var(--teal); color: var(--navy) }
.lp-step.done .lp-step-num { background: #22c55e; color: #fff }
.lp-step-label {
  font-size: 10px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: .05em;
}
.lp-step.active .lp-step-label { color: var(--navy) }

/* Main */
.lp-main { max-width: 760px; width: 100%; margin: 0 auto; padding: 28px 16px 60px; flex: 1; box-sizing: border-box }

/* Card base */
.lp-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 8px 40px rgba(26,52,97,.08);
  border: 1px solid rgba(26,52,97,.05);
}
.lp-card-pad { padding: 24px }
@media (min-width: 640px) { .lp-card-pad { padding: 32px } }

/* PASO 0: Segmentos */
.lp-grid-segs {
  display: grid; grid-template-columns: 1fr; gap: 14px;
}
@media (min-width: 640px) { .lp-grid-segs { grid-template-columns: 1fr 1fr } }
.lp-seg-card {
  background: #fff; border-radius: 20px;
  padding: 24px; text-align: left;
  border: 2px solid transparent;
  box-shadow: 0 4px 20px rgba(26,52,97,.06);
  cursor: pointer; transition: transform .18s, box-shadow .18s, border-color .18s;
  font-family: inherit;
  display: flex; flex-direction: column; gap: 6px;
}
.lp-seg-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(26,52,97,.12);
}
.lp-seg-card.is-elegido {
  border-color: var(--teal);
  background: linear-gradient(180deg, #fff 0%, #f0fcfd 100%);
  box-shadow: 0 8px 28px rgba(0,196,212,.22);
}
.lp-seg-icon { font-size: 36px; line-height: 1; margin-bottom: 4px }
.lp-seg-titulo { font-size: 18px; font-weight: 800; color: var(--navy) }
.lp-seg-sub { font-size: 13px; color: var(--teal); font-weight: 700 }
.lp-seg-det { font-size: 13px; color: var(--muted2); line-height: 1.5; margin-top: 4px }
.lp-seg-check {
  margin-top: 10px; align-self: flex-start;
  background: var(--teal); color: var(--navy);
  padding: 4px 12px; border-radius: 999px;
  font-size: 11px; font-weight: 800; letter-spacing: .03em;
}

/* PASO 1: Toggle Essential / Premium */
.lp-tier-info {
  background: #f0fcfd; border: 1px solid #b3eef3;
  border-radius: 12px; padding: 14px 16px;
  font-size: 13px; line-height: 1.55; color: var(--navy);
  margin-bottom: 18px;
}
.lp-toggle-wrap { display: flex; justify-content: center; margin-bottom: 22px }
.lp-toggle {
  background: #fff; border: 1px solid var(--line2);
  border-radius: 999px; padding: 5px;
  display: inline-flex;
  box-shadow: 0 2px 8px rgba(26,52,97,.04);
}
.lp-toggle button {
  padding: 9px 22px; border-radius: 999px;
  border: 0; background: transparent;
  font-size: 12px; font-weight: 800; letter-spacing: .06em;
  color: rgba(26,52,97,.5); cursor: pointer;
  transition: background .15s, color .15s;
  font-family: inherit;
}
.lp-toggle button.on { background: var(--navy); color: #fff }

/* Kits */
.lp-kits { display: flex; flex-direction: column; gap: 16px }
.lp-kit {
  position: relative;
  background: #fff; border: 2px solid var(--line2);
  border-radius: 18px; padding: 22px;
  transition: transform .18s, box-shadow .18s, border-color .18s;
}
.lp-kit:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(26,52,97,.08) }
.lp-kit.is-elegido {
  border-color: var(--teal);
  box-shadow: 0 0 0 4px rgba(0,196,212,.15);
}
.lp-kit.is-premium { background: linear-gradient(180deg, #fff 0%, #fafdff 100%) }
.lp-kit-badge {
  position: absolute; top: -10px; left: 18px;
  background: var(--teal); color: var(--navy);
  padding: 4px 12px; border-radius: 999px;
  font-size: 11px; font-weight: 800; letter-spacing: .03em;
}
.lp-kit-head { margin-bottom: 14px }
.lp-kit-titulo { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px }
.lp-kit-titulo h3 { font-size: 20px; font-weight: 900; color: var(--navy); margin: 0; letter-spacing: -.01em }
.lp-kit-tier {
  font-size: 10px; font-weight: 800; letter-spacing: .06em;
  padding: 3px 10px; border-radius: 999px;
}
.tier-essential { background: #e8f5e9; color: #2e7d32 }
.tier-premium   { background: #e0f7fa; color: #006064 }
.tier-comercial { background: #fff3e0; color: #e65100 }
.lp-kit-meta { font-size: 12px; color: var(--muted2); font-weight: 600 }
.lp-kit-desc { font-size: 14px; color: #475569; line-height: 1.5; margin: 8px 0 0 }

.lp-kit-precio-row {
  display: flex; justify-content: space-between; align-items: center;
  gap: 14px; padding: 14px 0;
  border-top: 1px dashed var(--line2);
  border-bottom: 1px dashed var(--line2);
  margin-bottom: 14px;
}
.lp-kit-precio { font-size: 28px; font-weight: 900; color: var(--navy); letter-spacing: -.02em; line-height: 1 }
.lp-kit-precio-sub { font-size: 11px; color: var(--muted2); font-weight: 600; margin-top: 3px }
.lp-kit-ver {
  background: transparent; border: 0;
  font-size: 13px; font-weight: 700; color: var(--teal);
  cursor: pointer; padding: 8px 12px; border-radius: 8px;
  font-family: inherit; flex-shrink: 0;
}
.lp-kit-ver:hover { background: #f0fcfd }

.lp-kit-productos {
  list-style: none; padding: 14px 16px; margin: 0 0 14px;
  background: #f8fafc; border-radius: 12px;
}
.lp-kit-productos li {
  font-size: 13px; padding: 6px 0;
  border-bottom: 1px solid var(--line2);
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px;
}
.lp-kit-productos li:last-child { border-bottom: 0 }
.lp-kit-prod-cant { font-weight: 800; color: var(--teal); min-width: 28px }
.lp-kit-prod-nom { font-weight: 600; color: var(--navy) }
.lp-kit-prod-pres, .lp-kit-prod-marca { font-size: 12px; color: var(--muted2) }

.lp-kit-btn, .lp-kit-btn-on {
  width: 100%; padding: 14px 18px;
  border-radius: 999px; font-size: 14px; font-weight: 800;
  cursor: pointer; transition: transform .12s, background .15s;
  font-family: inherit; letter-spacing: .02em;
}
.lp-kit-btn { background: var(--navy); color: #fff; border: 0 }
.lp-kit-btn:hover { background: var(--blue) }
.lp-kit-btn-on { background: var(--teal); color: var(--navy); border: 0 }
.lp-kit-btn-on:hover { background: #fff; box-shadow: 0 0 0 2px var(--teal) }

.lp-link-todos {
  display: block; margin: 22px auto 0; padding: 10px 16px;
  background: transparent; border: 0;
  font-size: 13px; font-weight: 700; color: var(--blue);
  cursor: pointer; font-family: inherit;
}
.lp-link-todos:hover { text-decoration: underline }

/* Form */
.lp-field { display: block; margin-bottom: 16px }
.lp-field b {
  display: block; font-size: 11px; font-weight: 800;
  color: var(--navy); letter-spacing: .08em; text-transform: uppercase;
  margin-bottom: 6px;
}
.lp-req { color: #dc2626; margin-left: 2px }
.lp-card input, .lp-card select, .lp-card textarea {
  width: 100%; box-sizing: border-box;
  padding: 12px 14px; font-size: 15px;
  border: 1.5px solid var(--line2); border-radius: 10px;
  background: #fff; color: var(--navy); font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.lp-card input:focus, .lp-card select:focus, .lp-card textarea:focus {
  outline: none; border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(0,196,212,.15);
}
.lp-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.lp-row-2-3 { display: grid; grid-template-columns: 2fr 1fr; gap: 12px }
@media (max-width: 520px) {
  .lp-row-2, .lp-row-2-3 { grid-template-columns: 1fr }
}

.lp-info {
  background: #f0fcfd; border: 1px solid #b3eef3;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: var(--navy); margin-bottom: 14px;
  line-height: 1.5;
}
.lp-info-pago {
  background: linear-gradient(135deg, #f0fcfd 0%, #e0f7fa 100%);
  border: 1px solid #b3eef3; border-radius: 12px;
  padding: 14px 16px; font-size: 13px; line-height: 1.5;
  color: var(--navy); margin-top: 18px;
}
.lp-err {
  background: #fef2f2; border: 1px solid #fca5a5;
  border-radius: 10px; padding: 12px 14px;
  font-size: 13px; color: #991b1b; margin-top: 14px;
}

/* Aviso de privacidad (footer del wizard) */
.lp-privacy {
  margin: 24px auto 0; padding: 12px 16px;
  font-size: 11px; line-height: 1.5;
  color: var(--muted2); text-align: center;
  background: rgba(248,250,252,.6);
  border-radius: 10px; max-width: 540px;
}

/* Warning barrio detectado vs elegido */
.lp-warn-barrio {
  display: flex; gap: 12px; align-items: flex-start;
  margin-top: 14px; padding: 14px 16px;
  border-radius: 12px; font-size: 13px;
}
.lp-warn-barrio.is-warn { background: #fef3c7; border: 1.5px solid #fcd34d; color: #78350f }
.lp-warn-barrio.is-bloqueo { background: #fef2f2; border: 1.5px solid #fca5a5; color: #7f1d1d }
.lp-warn-barrio-icon { font-size: 24px; line-height: 1; flex-shrink: 0 }
.lp-warn-barrio-body { flex: 1 }
.lp-warn-barrio-body strong { display: block; font-size: 14px; margin-bottom: 6px }
.lp-warn-barrio-body p { margin: 0 0 10px; line-height: 1.45 }
.lp-warn-cta {
  display: inline-block; padding: 10px 18px; border-radius: 999px;
  background: var(--navy); color: #fff !important; text-decoration: none;
  border: 0; font-weight: 800; font-size: 13px; cursor: pointer;
  font-family: inherit; margin-right: 8px;
}
.lp-warn-cta:hover { background: var(--blue) }
.lp-warn-link {
  display: block; margin-top: 10px; background: transparent; border: 0;
  color: inherit; opacity: .65;
  font-size: 11px; font-weight: 600; text-decoration: underline;
  cursor: pointer; font-family: inherit; padding: 4px 0;
}

.lp-confirm-pin {
  display: flex; align-items: center; gap: 10px; margin-top: 12px;
  padding: 12px 14px; border-radius: 10px;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.lp-confirm-pin.ok   { border: 1.5px solid #22c55e; background: #f0fdf4; color: #166534 }
.lp-confirm-pin.pend { border: 1.5px solid #f59e0b; background: #fefce8; color: #854d0e }
.lp-confirm-pin input { width: 16px !important; height: 16px; margin: 0 }

.lp-btn-secondary {
  padding: 11px 18px; font-size: 13px; font-weight: 700;
  background: #fff; color: var(--navy);
  border: 1.5px solid var(--navy); border-radius: 999px;
  cursor: pointer; font-family: inherit;
  transition: background .15s, color .15s;
}
.lp-btn-secondary:hover:not(:disabled) { background: var(--navy); color: #fff }
.lp-btn-secondary:disabled { opacity: .5; cursor: not-allowed }

/* Resumen */
.lp-resumen { display: flex; flex-direction: column }
.lp-row {
  display: flex; justify-content: space-between; gap: 16px;
  align-items: baseline;
  padding: 12px 0; border-bottom: 1px dashed var(--line2);
  font-size: 14px;
}
.lp-row:last-child { border-bottom: 0 }
.lp-row-tag {
  color: var(--muted2); font-weight: 700; font-size: 11px;
  text-transform: uppercase; letter-spacing: .06em; flex-shrink: 0;
}
.lp-row-val { text-align: right; color: var(--navy); font-weight: 500 }

/* Nav buttons */
.lp-nav-btns {
  display: flex; justify-content: space-between; gap: 10px;
  margin-top: 24px; padding-top: 18px;
  border-top: 1px solid var(--line2);
}
.lp-btn-back {
  padding: 12px 18px; background: transparent; border: 0;
  color: var(--muted2); font-weight: 700; font-size: 14px;
  cursor: pointer; border-radius: 999px; font-family: inherit;
}
.lp-btn-back:hover { background: #f1f5f9; color: var(--navy) }
.lp-btn-next {
  padding: 14px 26px; font-size: 15px; font-weight: 800;
  background: linear-gradient(135deg, var(--blue), var(--teal));
  color: #fff; border: 0; border-radius: 999px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 6px 20px rgba(0,196,212,.3);
  transition: transform .12s, box-shadow .12s, opacity .12s;
}
.lp-btn-next:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(0,196,212,.4);
}
.lp-btn-next:disabled { opacity: .45; cursor: not-allowed; box-shadow: none }

/* WhatsApp flotante */
.lp-wa {
  position: fixed; bottom: 24px; right: 24px; z-index: 60;
  background: #25d366; border-radius: 50%;
  width: 56px; height: 56px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 20px rgba(37,211,102,.45);
  transition: transform .15s;
  text-decoration: none;
}
.lp-wa:hover { transform: scale(1.08) }

/* FOOTER (idéntico a la web pública) */
.lp-footer { background: var(--navy); padding: 48px 16px 32px; margin-top: 0 }
.lp-footer-inner { max-width: 1100px; margin: 0 auto }
.lp-footer-grid {
  display: grid; grid-template-columns: 1fr;
  gap: 32px; margin-bottom: 32px;
}
@media (min-width: 640px) {
  .lp-footer-grid { grid-template-columns: 1.4fr 1fr 1fr }
}
.lp-footer-logo {
  font-size: 22px; font-weight: 900; color: #fff;
  margin-bottom: 10px; letter-spacing: -.02em;
}
.lp-footer-logo span { color: var(--teal) }
.lp-footer-desc {
  color: rgba(255,255,255,.5); font-size: 13px;
  line-height: 1.6; margin: 0;
}
.lp-footer-tit {
  color: #fff; font-weight: 700;
  font-size: 12px; text-transform: uppercase; letter-spacing: .08em;
  margin-bottom: 14px;
}
.lp-footer-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px }
.lp-footer-list a {
  color: rgba(255,255,255,.6); font-size: 13px;
  text-decoration: none; transition: color .15s;
}
.lp-footer-list a:hover { color: var(--teal) }
.lp-footer-bot {
  border-top: 1px solid rgba(255,255,255,.1);
  padding-top: 26px;
  display: flex; flex-direction: column; gap: 6px;
  align-items: center; text-align: center;
}
@media (min-width: 640px) {
  .lp-footer-bot { flex-direction: row; justify-content: space-between; text-align: left }
}
.lp-footer-bot p { margin: 0 }
.lp-footer-bot p:first-child { color: rgba(255,255,255,.4); font-size: 13px }
.lp-footer-bot p:last-child { color: rgba(255,255,255,.3); font-size: 11px }
`

/** Visitor → primero crear cuenta, después wizard de suscripción */
function PreSignup() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ nombre: '', email: '', phone: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!form.nombre.trim()) { setErr('Falta tu nombre'); return }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setErr('Email inválido'); return
    }
    if (form.password.length < 6) { setErr('La contraseña tiene que tener al menos 6 caracteres'); return }
    setLoading(true)
    try {
      const emailNormalizado = form.email.trim().toLowerCase()
      const { data: signUp, error } = await supabase.auth.signUp({
        email: emailNormalizado,
        password: form.password,
        options: {
          data: { nombre: form.nombre.trim(), phone: form.phone },
        },
      })
      if (error) {
        if (error.message?.toLowerCase().includes('already')) {
          setErr('Ya existe una cuenta con ese email. Iniciá sesión y volvé a /suscribirme.')
        } else {
          setErr('No pudimos crear tu cuenta: ' + error.message)
        }
        setLoading(false); return
      }
      // Si no devuelve sesión, forzamos signIn inmediato (trigger SQL auto-confirma email)
      if (!signUp?.session) {
        const { error: signInErr } = await supabase.auth.signInWithPassword({
          email: emailNormalizado,
          password: form.password,
        })
        if (signInErr) {
          setErr('Cuenta creada pero no pudimos iniciar sesión: ' + signInErr.message)
          setLoading(false); return
        }
      }
      // El AuthProvider detecta la nueva sesión y re-renderiza con el wizard
    } catch (e) {
      setErr(e.message); setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f1f3d 0%, #1a3461 50%, #1a5cb8 100%)', display: 'flex', flexDirection: 'column', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <nav style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1100, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        <a href="https://limpack.uy" style={{ color: '#fff', textDecoration: 'none', fontWeight: 800, fontSize: 22 }}>
          Lim<span style={{ color: '#00c4d4' }}>pack</span>
        </a>
        <Link to="/login" style={{
          color: '#fff', fontSize: 13, fontWeight: 700, textDecoration: 'none',
          padding: '8px 16px', border: '1.5px solid rgba(255,255,255,.35)',
          borderRadius: 999, transition: 'all .15s', whiteSpace: 'nowrap',
        }}>
          Ya tengo cuenta · Iniciar sesión →
        </Link>
      </nav>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 24px 48px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 480px)', gap: 48,
          maxWidth: 1000, width: '100%', alignItems: 'center',
        }} className="pre-grid">
          {/* Columna izq: claim + beneficios */}
          <div style={{ color: '#fff' }} className="pre-claim">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(0,196,212,.18)', color: '#7ce8f0', padding: '5px 14px', borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 14 }}>
              ⭐ Suscripción mensual
            </div>
            <h1 style={{ fontSize: 38, fontWeight: 900, letterSpacing: '-.02em', lineHeight: 1.1, margin: '0 0 18px' }}>
              Tu kit de limpieza<br />resuelto cada mes.
            </h1>
            <p style={{ color: 'rgba(255,255,255,.78)', fontSize: 15, lineHeight: 1.55, margin: '0 0 24px' }}>
              Elegís tu kit, lo recibís en tu puerta y olvidate de ir al super.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14 }}>
              <li style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,196,212,.18)', color: '#7ce8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>✓</span>
                Envío gratis los días de tu ruta
              </li>
              <li style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,196,212,.18)', color: '#7ce8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>✓</span>
                10% más barato que el supermercado
              </li>
              <li style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,196,212,.18)', color: '#7ce8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>✓</span>
                Pausá o modificá cuando quieras
              </li>
              <li style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,196,212,.18)', color: '#7ce8f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>✓</span>
                Bonificación de 3 meses al arrancar
              </li>
            </ul>
          </div>

          {/* Columna der: card form */}
          <div style={{ background: '#fff', borderRadius: 18, padding: '36px 32px', boxShadow: '0 20px 60px rgba(0,0,0,.25)', width: '100%', boxSizing: 'border-box' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(0,196,212,.12)', color: '#0e7490', padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 14 }}>
              ⭐ Suscripción mensual
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: '#1a3461', letterSpacing: '-.02em' }}>Empezá tu suscripción</h1>
            <p style={{ marginTop: 8, color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
              Primero creamos tu cuenta. Después armás tu Kit, elegís cuándo te lo llevamos y listo.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <SignupField label="Nombre completo" required>
              <input type="text" required value={form.nombre}
                onChange={e => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: María González"
                autoComplete="name" />
            </SignupField>
            <SignupField label="Email" required>
              <input type="email" required value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="tu@email.com"
                autoComplete="email" />
            </SignupField>
            <SignupField label="WhatsApp" required>
              <input type="tel" required value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="099 xxx xxx"
                autoComplete="tel" />
            </SignupField>
            <SignupField label="Contraseña" required>
              <input type="password" required minLength={6}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                autoComplete="new-password" />
            </SignupField>

            {err && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#7f1d1d', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 12 }}>
                {err}
              </div>
            )}

            <button type="submit" disabled={loading}
              style={{
                width: '100%', padding: '14px 20px', fontSize: 15, fontWeight: 800,
                background: 'linear-gradient(135deg, #1a5cb8, #00c4d4)', color: '#fff', border: 0,
                borderRadius: 999, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: loading ? .6 : 1, marginTop: 6,
                boxShadow: '0 6px 18px rgba(0,196,212,.3)',
              }}>
              {loading ? 'Creando tu cuenta…' : 'Crear cuenta y continuar →'}
            </button>

            <p style={{ marginTop: 16, fontSize: 11, color: '#64748b', textAlign: 'center', lineHeight: 1.5 }}>
              🔒 Tus datos están protegidos. Solo los usamos para procesar tu suscripción y nunca los compartimos.
            </p>
          </form>
        </div>
        </div>
      </main>

      <style>{`
        @media (max-width: 820px) {
          .pre-grid { grid-template-columns: 1fr !important; gap: 24px !important }
          .pre-claim h1 { font-size: 28px !important }
          .pre-claim { text-align: center }
          .pre-claim ul { max-width: 320px; margin: 0 auto !important }
        }
      `}</style>
    </div>
  )
}

function SignupField({ label, required, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#1a3461', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </b>
      {children && (
        <div style={{ marginTop: 0 }}>
          {children}
        </div>
      )}
      <style>{`
        label > div input {
          width: 100%; box-sizing: border-box;
          padding: 11px 13px; font-size: 14px;
          border: 1.5px solid #e2e8f0; border-radius: 10px;
          background: #fff; color: #1a3461;
          font-family: inherit;
          transition: border-color .15s, box-shadow .15s;
        }
        label > div input:focus {
          outline: none; border-color: #00c4d4;
          box-shadow: 0 0 0 3px rgba(0,196,212,.15);
        }
      `}</style>
    </label>
  )
}
